const STOREFRONT_ACCESS_TOKEN =
  window.Shopify?.shop === "felixgray-dev.myshopify.com"
    ? "d590f7327ecf61cd814a85c4d04fb022"
    : "6955e896373911e6da77cb894fada3a8";

// Simple in-memory cache to avoid refetching
const metaobjectCache = {};

// Helper to determine which image list to use based on product attributes
function getTargetFieldKey(frameSize, lensColor) {
  // Verbose Logging for Debugging
  console.group("[Storefront Fetcher] getTargetFieldKey Debug");
  console.log("Input frameSize:", frameSize);
  console.log("Input lensColor:", lensColor);
  console.log("Window Location Search:", window.location.search);

  // Check for active amber filter in URL
  const urlParams = new URLSearchParams(window.location.search);
  let hasAmberFilter = false;

  // Iterate through all parameters to find filter options
  for (const [key, value] of urlParams.entries()) {
    console.log(`Checking param: key="${key}", value="${value}"`);
    // Check for standard filter parameters (e.g., filter.v.option.color=Amber)
    if (key.includes("filter") && value.toLowerCase().includes("amber")) {
      hasAmberFilter = true;
      console.log(">> Match found for Amber filter!");
      // Don't break immediately, log all for completeness if needed? No, break is fine.
      break;
    }
    // Also check for GID if the user mentioned it specifically
    // filter.v.t.shopify.lens-color=gid://shopify/Metaobject/162978595002
    if (key.includes("filter") && value.includes("162978595002")) {
      hasAmberFilter = true;
      console.log(">> Match found for Amber filter via GID!");
      break;
    }
  }

  let targetField = "clear_images_adults"; // Default

  if (!lensColor) lensColor = "clear";
  const fs = frameSize ? frameSize.toLowerCase() : "";
  const lc = lensColor.toLowerCase();

  console.log("Normalized frameSize:", fs);
  console.log("Normalized lensColor:", lc);
  console.log("Has Amber Filter:", hasAmberFilter);

  // If amber filter is active, force amber images unless logic dictates otherwise
  if (hasAmberFilter) {
    if (fs.includes("small") || fs.includes("kids small")) {
      console.log("Returning: amber_images_kids_small");
      console.groupEnd();
      return "amber_images_kids_small";
    }
    if (fs.includes("large") || fs.includes("kids large")) {
      console.log("Returning: amber_images_kids_large");
      console.groupEnd();
      return "amber_images_kids_large";
    }
    console.log("Returning: amber_images (default)");
    console.groupEnd();
    return "amber_images"; // Default to adult amber if just "amber" or adult size
  }

  // Logic mirroring the Liquid implementation
  if (fs.includes("adult")) {
    if (lc.includes("clear")) return "clear_images_adults";
    if (lc.includes("amber")) return "amber_images_adults";
    if (lc.includes("polarized")) return "polarized_images_adults";
  } else if (fs.includes("small") || fs.includes("kids small")) {
    if (lc.includes("clear")) return "clear_images_kids_small";
    if (lc.includes("amber")) return "amber_images_kids_small";
  } else if (fs.includes("large") || fs.includes("kids large")) {
    if (lc.includes("clear")) return "clear_images_kids_large";
    if (lc.includes("amber")) return "amber_images_kids_large";
  } else {
    // Fallback based on lens color only
    if (lc.includes("clear")) return "clear_images_adults";
    if (lc.includes("amber")) return "amber_images_adults";
    if (lc.includes("polarized")) return "polarized_images_adults";
  }

  return targetField;
}

// Fetch function using Storefront API
async function fetchMetaobjectImages(handle) {
  if (metaobjectCache[handle]) return metaobjectCache[handle];

  const query = `
    query MetaobjectQuery($handle: String!) {
      metaobject(handle: { handle: $handle, type: "frames" }) {
        fields {
          key
          type
          value
          references(first: 10) {
            nodes {
              ... on MediaImage {
                image {
                  url(transform: { maxWidth: 1000 })
                  altText
                  width
                  height
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(
      `https://${window.Shopify.shop}/api/2024-01/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({
          query,
          variables: { handle },
        }),
      },
    );

    const result = await response.json();
    if (result.errors) {
      console.warn("[Metaobject Fetcher] GraphQL Errors:", result.errors);
      // Don't return null yet; perhaps some fields worked?
    }

    const fields = result.data?.metaobject?.fields || [];
    const fieldsMap = {};
    const fallbackIds = []; // To collect GIDs if direct references fail
    const fieldGidMap = {}; // Map field key to array of GIDs

    fields.forEach((field) => {
      // 1. Try direct references
      let images =
        field.references?.nodes?.map((node) => node.image).filter(Boolean) ||
        [];

      // 2. Fallback: If no images found but value is present and looks like a list of GIDs
      if (
        images.length === 0 &&
        field.value &&
        field.type === "list.file_reference"
      ) {
        try {
          const gids = JSON.parse(field.value);
          if (Array.isArray(gids) && gids.length > 0) {
            // Store GIDs for batch fetching
            fallbackIds.push(...gids);
            fieldGidMap[field.key] = gids;
            // Mark as empty initially
            fieldsMap[field.key] = [];
          }
        } catch (e) {
          console.warn(
            "[Metaobject Fetcher] Failed to parse value for fallback:",
            field.key,
            e,
          );
        }
      } else if (images.length > 0) {
        fieldsMap[field.key] = images;
      }
    });

    // If we have GIDs to fetch manually
    if (fallbackIds.length > 0) {
      // Deduplicate IDs
      const uniqueIds = [...new Set(fallbackIds)];

      const nodesQuery = `
        query NodesQuery($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on MediaImage {
              id
              image {
                url(transform: { maxWidth: 1000 })
                altText
                width
                height
              }
            }
          }
        }
      `;

      const nodesResponse = await fetch(
        `https://${window.Shopify.shop}/api/2024-01/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
          },
          body: JSON.stringify({
            query: nodesQuery,
            variables: { ids: uniqueIds },
          }),
        },
      );

      const nodesResult = await nodesResponse.json();

      if (nodesResult.data && nodesResult.data.nodes) {
        const nodesMap = {};
        nodesResult.data.nodes.forEach((node) => {
          if (node && node.id && node.image) {
            nodesMap[node.id] = node.image;
          }
        });

        // Hydrate the fieldsMap with the fetched images
        Object.keys(fieldGidMap).forEach((key) => {
          const gids = fieldGidMap[key];
          const resolvedImages = gids
            .map((gid) => nodesMap[gid])
            .filter(Boolean);
          if (resolvedImages.length > 0) {
            fieldsMap[key] = resolvedImages;
          }
        });
      }
    }

    metaobjectCache[handle] = fieldsMap;
    return fieldsMap;
  } catch (error) {
    console.error("[Metaobject Fetcher] Network/Fetch error:", error);
    return null;
  }
}

// Helper to update image src and srcset
function updateImage(imgElement, imageData) {
  if (!imgElement || !imageData) return;

  // Construct new URL (ensure no existing params if problematic, but usually fine)
  const baseUrl = imageData.url;

  imgElement.src = baseUrl;

  // Update srcset if present
  if (imgElement.hasAttribute("srcset")) {
    // Simple srcset generation matching typical Shopify patterns
    const srcset = `
        ${baseUrl}&width=200 200w,
        ${baseUrl}&width=300 300w,
        ${baseUrl}&width=400 400w,
        ${baseUrl}&width=500 500w,
        ${baseUrl}&width=600 600w,
        ${baseUrl}&width=700 700w,
        ${baseUrl}&width=800 800w,
        ${baseUrl}&width=900 900w,
        ${baseUrl}&width=1000 1000w
      `;
    imgElement.srcset = srcset;
  }

  // Update dimensions to prevent layout shift if ratios differ slightly
  if (imageData.width) imgElement.width = imageData.width;
  if (imageData.height) imgElement.height = imageData.height;
}

// Fetch Badges using Storefront API
async function fetchVariantBadges(variantId) {
  if (!variantId) return [];

  // Ensure GID format
  const gid = variantId.includes("gid://")
    ? variantId
    : `gid://shopify/ProductVariant/${variantId}`;

  const query = `
    query VariantBadges($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          variantBadges: metafield(namespace: "custom", key: "badges") {
            value
          }
          product {
            productBadges: metafield(namespace: "custom", key: "badges") {
              value
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(
      `https://${window.Shopify.shop}/api/2024-01/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({
          query,
          variables: { id: gid },
        }),
      },
    );

    const result = await response.json();

    if (result.errors) {
      console.warn("[Badge Fetcher] GraphQL Errors:", result.errors);
      return [];
    }

    const node = result.data?.node;
    if (!node) return [];

    let badges = [];

    // Helper to process badge value (JSON array or raw string)
    const processBadgeValue = (val, source) => {
      if (!val) return;
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) {
          badges.push(...parsed);
        } else {
          // Parsed but not an array? Treat as single raw string
          badges.push(val);
        }
      } catch (e) {
        // SyntaxError means it's likely a plain string like "Limited Edition"
        badges.push(val);
      }
    };

    // Parse product badges
    if (node.product?.productBadges?.value) {
      processBadgeValue(node.product.productBadges.value, "Product");
    }

    // Parse variant badges
    if (node.variantBadges?.value) {
      processBadgeValue(node.variantBadges.value, "Variant");
    }

    // Deduplicate
    const finalBadges = [...new Set(badges)];
    return finalBadges;
  } catch (e) {
    console.warn("[Badge Fetcher] Failed to fetch badges:", e);
    return [];
  }
}

// On page load, hydrate any cards the server couldn't resolve metaobject images for
async function fetchMissingCards() {
  const missingCards = document.querySelectorAll(
    'product-card[data-metaobject-status="missing"]',
  );
  if (missingCards.length === 0) return;

  for (const card of missingCards) {
    const handle = card.dataset.metaobjectHandle;
    if (!handle) continue;

    // Mark to prevent duplicate runs
    card.dataset.metaobjectStatus = "fetching";

    const data = await fetchMetaobjectImages(handle);
    if (!data) {
      card.dataset.metaobjectStatus = "error";
      continue;
    }

    const frameSize = card.dataset.frameSize;
    const lensColor = card.dataset.lensColor || "clear";
    let targetKey = getTargetFieldKey(frameSize, lensColor);
    let images = data[targetKey];

    // Kids size fallback (mirror Liquid logic)
    if (!images || images.length === 0) {
      if (targetKey === "clear_images_kids_small")
        images = data["clear_images_kids_large"];
      else if (targetKey === "clear_images_kids_large")
        images = data["clear_images_kids_small"];
      else if (targetKey === "amber_images_kids_small")
        images = data["amber_images_kids_large"];
      else if (targetKey === "amber_images_kids_large")
        images = data["amber_images_kids_small"];
    }

    if (images && images.length > 0) {
      const primaryImg = card.querySelector(".product-card__image--primary");
      const secondaryImg = card.querySelector(
        ".product-card__image--secondary",
      );
      updateImage(primaryImg, images[0]);
      if (images.length > 1 && secondaryImg) {
        updateImage(secondaryImg, images[1]);
      }
      card.dataset.metaobjectStatus = "fetched";
    } else {
      card.dataset.metaobjectStatus = "failed";
    }
  }
}

document.addEventListener("DOMContentLoaded", fetchMissingCards);
document.addEventListener("shopify:section:load", fetchMissingCards);

// Main Event Listener
document.addEventListener("change", async (event) => {
  const input = event.target;
  // Match inputs inside product-card that start with swatch-
  if (!input.matches('.product-card input[name^="swatch-"]')) return;

  const card = input.closest("product-card");
  if (!card) return;

  // 1. Update Badges
  const variantId = input.dataset.variantId;

  if (variantId) {
    fetchVariantBadges(variantId).then((badges) => {
      updateBadges(card, badges);
    });
  } else {
    // Fallback to data attribute
    const badgesData = input.dataset.badges;
    if (badgesData) {
      try {
        const badges = JSON.parse(badgesData);
        updateBadges(card, badges);
      } catch (e) {
        console.warn("Failed to update badges from data attribute:", e);
      }
    }
  }

  // 2. Fetch Metaobject Images (if handle exists)
  const handle = input.dataset.metaobjectHandle.replace("-kids",""); // Normalize handle if needed
  if (!handle) return;

  // Visual feedback
  card.style.opacity = "0.6";
  card.style.transition = "opacity 0.2s";

  const data = await fetchMetaobjectImages(handle);

  // Remove feedback
  card.style.opacity = "1";

  if (!data) {
    console.warn("[Metaobject Fetcher] No data found for handle:", handle);
    return;
  }

  // Determine key
  const frameSize = card.dataset.frameSize;
  const lensColor = card.dataset.lensColor || "clear";

  let targetKey = getTargetFieldKey(frameSize, lensColor);
  let images = data[targetKey];
console.log("data key" , data,"targetKey", targetKey, "images", images);
  // Logic fallback (kids small vs large)
  if (!images || images.length === 0) {
    if (targetKey === "clear_images_kids_small")
      images = data["clear_images_kids_large"];
    else if (targetKey === "clear_images_kids_large")
      images = data["clear_images_kids_small"];
    else if (targetKey === "amber_images_kids_small")
      images = data["amber_images_kids_large"];
    else if (targetKey === "amber_images_kids_large")
      images = data["amber_images_kids_small"];
  }

  if (images && images.length > 0) {
    const primaryImg = card.querySelector(".product-card__image--primary");
    const secondaryImg = card.querySelector(".product-card__image--secondary");

    // Update Primary
    updateImage(primaryImg, images[0]);

    // Update Secondary
    if (images.length > 1 && secondaryImg) {
      updateImage(secondaryImg, images[1]);
    }
  } else {
    console.warn("[Metaobject Fetcher] No images found for key:", targetKey);
  }
});

function updateBadges(card, badges) {
  let badgeList = card.querySelector("badge-list");
  const isVertical = card.dataset.badgesVertical === "true";
  const placement = card.dataset.badgesPlacement || "place-self-start";

  // If no badge list exists but we have badges, create one
  if (!badgeList && badges && badges.length > 0) {
    const figure = card.querySelector(".product-card__figure");
    if (figure) {
      badgeList = document.createElement("badge-list");

      // Calculate alignment suffix logic mirroring Liquid
      // liquid: assign badges_alignment = settings.product_card_badges_placement | split: '-' | last
      let alignment = placement.split("-").pop();

      // liquid: if settings.product_card_show_badges_vertically ... assign badges_alignment = settings.product_card_badges_placement | remove: 'place-self-' | split: '-' | first
      if (isVertical) {
        const simplePlacement = placement.replace("place-self-", "");
        alignment = simplePlacement.split("-")[0];
      }

      const classes = ["badge-list", placement];

      // liquid: {% unless vertical_text %}badge-list--{{ direction | default: 'row' }}{% endunless %}
      // We assume direction='column' as per product-card.liquid
      if (!isVertical) {
        classes.push("badge-list--column");
      }

      // liquid: {% if direction == 'column' %}align-{{ alignment }}{% else %}justify-{{ alignment }}{% endif %}
      // We assume direction='column'
      classes.push(`align-${alignment}`);

      badgeList.className = classes.join(" ");

      // Insert into figure (usually at the start)
      figure.prepend(badgeList);
    } else {
      // Fallback to info if figure missing
      const info = card.querySelector(".product-card__info");
      if (info) {
        badgeList = document.createElement("badge-list");
        badgeList.className =
          "badge-list product-badges badge-list--row justify-start";
        info.prepend(badgeList);
      }
    }
  }

  if (badgeList) {
    // Remove existing custom badges
    const existing = badgeList.querySelectorAll(".badge--custom");
    existing.forEach((el) => el.remove());

    // Add new ones
    if (badges && badges.length > 0) {
      const fragment = document.createDocumentFragment();
      badges.forEach((text) => {
        const span = document.createElement("span");
        span.className = "badge badge--custom";
        if (isVertical) span.classList.add("vertical-text");

        // Simple handleize: lowercase, replace spaces/symbols with hyphens
        const handle = text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        span.classList.add("badge--" + handle);
        span.textContent = text;
        fragment.appendChild(span);
      });
      badgeList.prepend(fragment);
    }

    // Hide if empty (though keep element if other badges exist like sold-out)
    if (badgeList.children.length === 0) {
      badgeList.style.display = "none";
    } else {
      badgeList.style.display = "";
    }
  }
}
